/**
 * Disclosure Editions Admin Agent — API client for the Admin hub's
 * "Disclosure editions" tab (statutory disclosure-form editions: WI RECR,
 * WI VLDR, ...). Admins can view every edition and change its STATUS only;
 * the form text is never edited in the app.
 *
 * Contract (backend/app/routes/admin_disclosure_editions.py):
 *   GET  /api/admin/disclosure-editions
 *        → { today, forms: [{ jurisdiction, form, title, statutory_ref, published,
 *            current_edition: {id, edition, version}|null, file_fallback,
 *            listings: {live, live_without_report, draft}, editions: [EditionSummary] }] }
 *   GET  /api/admin/disclosure-editions/history?jurisdiction&form&limit → { history: [Change] }
 *   GET  /api/admin/disclosure-editions/{id}
 *        → EditionSummary + { title, parse_error, issuable_problem, item_count,
 *            sections: [{code, heading, items: [...]}], source_yaml, history }
 *   GET  /api/admin/disclosure-editions/{id}/preview?to_status=active|retired
 *        → { action, from_status, to_status, blocked_reasons, current_before,
 *            current_after, changes_current, leaves_no_active_edition,
 *            live_listings, live_listings_without_report, draft_listings,
 *            requires_confirmation (true only when no edition would be active and
 *            live_listings_without_report > 0),
 *            after_issuable_problem, completed_reports_unaffected }
 *   POST /api/admin/disclosure-editions/{id}/status
 *        { to_status, note, confirm_no_active, expected_status } → { edition, change, effect }
 *        422 note_required | validation_error; 409 status_changed |
 *        invalid_transition | activation_blocked {problems} |
 *        no_active_edition {live_listings_without_report, live_listings, draft_listings}
 *
 * Errors come from `APIAgent.request` (`.status` and `.detail = {code, message, ...}`).
 */
import APIAgent from './apiAgent';

const BASE = '/api/admin/disclosure-editions';

export const disclosureEditionsAdminAgent = {
  /** Every form with its editions. */
  list() {
    return APIAgent.request(BASE, 'GET');
  },

  /** One edition with its full text, read-only. */
  get(editionId) {
    return APIAgent.request(`${BASE}/${encodeURIComponent(editionId)}`, 'GET');
  },

  /**
   * What a status change would do, without changing anything.
   * @param {number} editionId
   * @param {'active'|'retired'} toStatus
   */
  preview(editionId, toStatus) {
    const params = new URLSearchParams({ to_status: toStatus });
    return APIAgent.request(`${BASE}/${encodeURIComponent(editionId)}/preview?${params}`, 'GET');
  },

  /**
   * Change an edition's status (audited; a note is required).
   * @param {number} editionId
   * @param {object} args
   * @param {'active'|'retired'} args.toStatus
   * @param {string} args.note
   * @param {boolean} [args.confirmNoActive=false]
   * @param {string} [args.expectedStatus] - the status the admin saw
   */
  changeStatus(editionId, { toStatus, note, confirmNoActive = false, expectedStatus }) {
    return APIAgent.request(`${BASE}/${encodeURIComponent(editionId)}/status`, 'POST', {
      to_status: toStatus,
      note: note ? note.trim() : note,
      confirm_no_active: Boolean(confirmNoActive),
      expected_status: expectedStatus || null,
    });
  },
};

export default disclosureEditionsAdminAgent;
